import { cn } from '@/utils/cn';
import {
  formatAutoBiddingSummary,
  supportsAutoBidding,
  type AutoBiddingSettings,
} from '@/utils/autoBidding';

export function AutoBiddingSwitch({
  enabled,
  adProduct,
  disabled,
  onClick,
}: {
  enabled: boolean;
  adProduct: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  const supported = supportsAutoBidding(adProduct);
  const checked = supported && enabled;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="Auto bidding"
      title={supported ? 'Auto bidding' : 'Auto bidding is available for SP and SB'}
      disabled={disabled || !supported}
      onClick={onClick}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 align-middle rounded-full transition-colors',
        checked ? 'bg-brand' : 'bg-border',
        (disabled || !supported) && 'cursor-not-allowed opacity-50'
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-surface shadow transition-transform',
          checked && 'translate-x-4'
        )}
      />
    </button>
  );
}

export function AutoBiddingColumnCell({
  enabled,
  adProduct,
  settings,
  disabled,
  onOpen,
}: {
  enabled: boolean;
  adProduct: string;
  settings?: AutoBiddingSettings | null;
  disabled?: boolean;
  onOpen: () => void;
}) {
  const summary = enabled ? formatAutoBiddingSummary(settings) : '';
  return (
    <div className="flex flex-col items-start gap-1">
      <AutoBiddingSwitch
        enabled={enabled}
        adProduct={adProduct}
        disabled={disabled}
        onClick={onOpen}
      />
      {summary ? (
        <p className="max-w-[12rem] text-[11px] leading-snug text-foreground-muted">{summary}</p>
      ) : null}
      {enabled && supportsAutoBidding(adProduct) ? (
        <button
          type="button"
          className="text-[11px] font-medium text-brand hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={onOpen}
        >
          Edit
        </button>
      ) : null}
    </div>
  );
}
